/**
 * Stripe Credit Note v2 — frontend types + eligibility fetcher.
 *
 * Mirrors the backend payload from `StripePaymentService::getInvoiceCreditEligibility()`
 * and the controller endpoint
 * `GET customer-orders/{order_id}/invoices/{order_item_invoice_id}/credit-note-eligibility`.
 *
 * Used by Task 16169 (split button + invoice picker) and Task 16178 (wizard).
 */

import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';

export type CreditNoteDisableReason = 'voided' | 'draft' | 'fully_credited' | null;
export type CreditNotePaymentPath = 'card_refund' | 'out_of_band' | 'unpaid' | null;

export interface CreditNoteInvoiceLine {
  id: string;
  description: string | null;
  quantity: number;
  unit_amount_minor: number;
  amount_minor: number;
  currency: string;
  /** Sum of `quantity` across all existing credit notes for this `il_xxx`. */
  already_credited_qty: number;
  /** Sum of `amount` (minor units) across all existing credit notes for this `il_xxx`. */
  already_credited_amount_minor: number;
  /** `quantity - already_credited_qty`, clamped to ≥ 0. */
  remaining_qty: number;
  /** `amount_minor - already_credited_amount_minor`, clamped to ≥ 0. */
  remaining_amount_minor: number;
  /**
   * AB#21269 — Stripe locks each invoice line to the credit basis first used against it: after a
   * quantity-based credit note line, every later credit for that line must be quantity-based too
   * (and vice versa). `null`/absent = never credited, either mode allowed. Optional so older
   * backend responses (pre-rollout) still type-check.
   */
  credit_basis?: 'quantity' | 'amount' | null;
}

export interface CreditNoteExistingCnSummary {
  id: string;
  number: string | null;
  amount_minor: number;
  status: string | null;
  created: number;
  /**
   * Settlement allocation recorded at creation (AB#21269): `refund_amount` > 0 = refunded to card,
   * `out_of_band_amount` > 0 = manual settlement, both null = pre-payment (reduced amount due).
   * Absent on older backend responses and on CNs with no local row — UI shows no settlement tag.
   */
  refund_amount?: number | string | null;
  out_of_band_amount?: number | string | null;
  reason: string | null;
  memo: string | null;
  pdf: string | null;
  /**
   * AB#20717 — Stripe PDF S3-serving. `'s3'` when `pdf` is the backend's stored,
   * signed S3 copy; `'stripe'` when it fell back to Stripe's own link. Optional:
   * older payloads / rows predating the rollout omit it entirely.
   */
  pdf_source?: 's3' | 'stripe';
}

export interface CreditNoteInvoiceSummary {
  stripe_invoice_id: string;
  status: string;
  total_minor: number;
  amount_paid_minor: number;
  amount_remaining_minor: number;
  pre_payment_credit_notes_amount: number;
  post_payment_credit_notes_amount: number;
}

export interface CreditNoteEligibility {
  can_credit: boolean;
  disable_reason: CreditNoteDisableReason;
  remaining_creditable_minor: number;
  remaining_creditable: number;
  currency: string;
  payment_path: CreditNotePaymentPath;
  invoice: CreditNoteInvoiceSummary;
  lines: CreditNoteInvoiceLine[];
  existing_credit_notes: CreditNoteExistingCnSummary[];
  /**
   * AB#21269 — parallel backend contract addition. Optional so older backend responses
   * (pre-rollout) still type-check; treat as `false` when absent. `true` means Stripe
   * automatic tax is on for this invoice, which disallows `custom_line_item`s server-side —
   * the free-form section renders disabled with an explainer in that case.
   */
  automatic_tax?: boolean;
  /** AB#21269 — optional; `true` when the invoice has only been partially paid so far. */
  partially_paid?: boolean;
  /**
   * AB#21269 — optional; when present this is the authoritative creditable cap (may differ
   * from `remaining_creditable_minor` once tax is factored in) — prefer it over
   * `remaining_creditable_minor` for over-cap comparisons when available.
   */
  max_creditable_minor?: number;
}

// ---------------------------------------------------------------------------
// WS1 — credit note preview (AB#21269). New backend endpoint:
// `POST customer-orders/{order_id}/credit-notes/preview` — same payload shape as the create
// endpoint minus `idempotency_key`. Returns the tax-inclusive total plus payment-path
// allocation so the wizard can show real numbers before commit. Older backends 404 on this
// route — callers feature-detect once per modal open and fall back to the legacy pre-tax
// footer (see `CreateStripeCreditNoteModal.vue`).
// ---------------------------------------------------------------------------

export interface CreditNotePreviewAllocation {
  type: string;
  amount_minor: number;
}

export interface CreditNotePreview {
  total_minor: number;
  total_excluding_tax_minor: number;
  tax_minor: number;
  currency: string;
  payment_path: CreditNotePaymentPath;
  allocation: CreditNotePreviewAllocation;
  pre_payment_amount_minor: number;
  post_payment_amount_minor: number;
  remaining_creditable_minor: number;
  max_creditable_minor: number;
}

/**
 * Calls the preview endpoint and returns the parsed result. Deliberately does NOT call
 * `handleResponseException` (unlike {@link fetchInvoiceCreditEligibility}) — the wizard calls
 * this continuously as the user edits the form (debounced), including expected-422 validation
 * states and 404 feature-detection, so a toast per failed preview would be noisy. Callers
 * handle the thrown error inline (see `previewState` / `creditNoteErrorFromResponse`).
 */
export async function previewCreditNote(
  orderId: number,
  payload: Record<string, unknown>,
): Promise<CreditNotePreview> {
  // Backend `sendJsonResponse($message, $data)` wraps the payload under `result` (NOT `data`),
  // and nests the preview under `result.preview` (house style, like eligibility's `result.eligibility`).
  // Accept a flat `result` too, defensively.
  const response = await http.post<{
    message: string;
    result: CreditNotePreview | { preview: CreditNotePreview };
  }>(`customer-orders/${orderId}/credit-notes/preview`, payload);
  const result = response.data.result as Record<string, unknown>;
  return (result?.preview ?? result) as CreditNotePreview;
}

/**
 * Fetches Stripe credit-note eligibility + lines for one ledger row (order_item_invoices.id).
 * Throws on network/Stripe failure (caller is expected to surface the error to the user).
 */
export async function fetchInvoiceCreditEligibility(
  orderId: number,
  orderItemInvoiceId: number,
): Promise<CreditNoteEligibility> {
  try {
    // Backend `sendJsonResponse($message, $data)` wraps the payload under `result` (NOT `data`).
    // Shape: `{ message: string, result: { eligibility: ... }, errors: [] }`.
    const response = await http.get<{ message: string; result: { eligibility: CreditNoteEligibility } }>(
      `customer-orders/${orderId}/invoices/${orderItemInvoiceId}/credit-note-eligibility`,
    );
    return response.data.result.eligibility;
  } catch (err) {
    handleResponseException(err);
    throw err;
  }
}

/**
 * US 5253 — credit notes need a real Stripe invoice. Saga rows still in `pending_stripe_sync`
 * or `failed` carry `stripe_invoice_id = null` and would 422 on every eligibility call, so they
 * are excluded from all credit-note entry points (menu item, fast path, picker).
 *
 * Shared by `PriceBreakdownModal.vue` (`creditableInvoicesList`) and
 * `SelectStripeInvoiceForCreditNoteModal.vue` (`loadEligibility` defense-in-depth filter).
 */
export function isCreditableInvoiceRow(inv: Record<string, unknown> | null | undefined): boolean {
  return Number((inv as { id?: unknown })?.id ?? 0) > 0 && !!(inv as { stripe_invoice_id?: unknown })?.stripe_invoice_id;
}

/**
 * Human-readable label for a disable reason — used for tooltips and toasts.
 */
export function disableReasonLabel(reason: CreditNoteDisableReason): string {
  switch (reason) {
    case 'voided':
      return 'Invoice was voided.';
    case 'draft':
      return 'Invoice is not finalized yet.';
    case 'fully_credited':
      return 'Invoice is already fully credited.';
    default:
      return '';
  }
}

/**
 * Convert minor units → display major units using the same zero-decimal-currency list as
 * `StripePaymentService::amountFromStripeMinorUnits()` so frontend totals match the backend.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

export function minorUnitsToAmount(minor: number, currency: string): number {
  if (ZERO_DECIMAL_CURRENCIES.has((currency || '').toUpperCase())) {
    return Number(minor);
  }
  return Math.round((Number(minor) / 100) * 100) / 100;
}

/**
 * Inverse of {@link minorUnitsToAmount}: display major-unit value → Stripe minor units, with
 * the same zero-decimal-currency awareness as the backend `amountToStripeMinorUnits()`.
 * NaN / non-finite inputs round-trip to 0.
 */
export function amountToMinorUnits(major: number, currency: string): number {
  const value = Number(major);
  if (!Number.isFinite(value)) return 0;
  if (ZERO_DECIMAL_CURRENCIES.has((currency || '').toUpperCase())) {
    return Math.max(0, Math.round(value));
  }
  return Math.max(0, Math.round(value * 100));
}

/**
 * Format minor units → human display string `"1,234.56"` (no currency code suffix; caller appends).
 * Zero-decimal currencies skip the decimal part.
 */
export function formatMinorAsDisplay(minor: number, currency: string): string {
  const amount = minorUnitsToAmount(minor, currency);
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has((currency || '').toUpperCase());
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: isZeroDecimal ? 0 : 2,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  });
}

/** Mirrors the Stripe enum allowed by the backend. */
export const CREDIT_NOTE_REASONS = [
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'order_change', label: 'Order change' },
  { value: 'product_unsatisfactory', label: 'Product unsatisfactory' },
] as const;

export type CreditNoteReason = typeof CREDIT_NOTE_REASONS[number]['value'];

/**
 * Stable display copy keyed by `payment_path`. Used by the wizard's inline success view + by the
 * fallback toast path. Card-paid never shows the finance@ message; non-card paid always does;
 * unpaid never mentions refunds (per US 5253 / Task 16179 AC).
 */
export function paymentPathOutcomeMessage(path: string | null | undefined): string {
  switch (path) {
    case 'card_refund':
      return 'Credit note created. Refund issued to the original card.';
    case 'out_of_band':
      return 'Credit note created. Send a message to finance@custimoo.com if we should do a refund.';
    case 'unpaid':
      return 'Credit note applied. The invoice’s amount due has been reduced.';
    default:
      return 'Credit note created.';
  }
}

/**
 * Heuristic: detect when a backend / Stripe error during credit-note creation is a refund-side
 * failure (charge too old, partially refunded already, payment source no longer valid, etc.).
 * Used by the wizard's inline error view to surface actionable guidance instead of a generic
 * Stripe error toast.
 */
export function isLikelyRefundFailure(
  errorMessage: string | null | undefined,
  paymentPath: string | null | undefined,
): boolean {
  if (paymentPath !== 'card_refund') return false;
  if (!errorMessage) return false;
  const msg = errorMessage.toLowerCase();
  return (
    msg.includes('refund')
    || msg.includes('charge_disputed')
    || msg.includes('charge too old')
    || msg.includes('partially refunded')
    || msg.includes('refund_amount')
    || msg.includes('payment source')
  );
}

// ---------------------------------------------------------------------------
// WS2 — input integrity: reject-over-cap validation + qty parsing helpers
// (extracted from CreateStripeCreditNoteModal.vue so they're unit-testable).
// ---------------------------------------------------------------------------

export type LineEntryMode = 'qty' | 'amount';

export interface LineEntryValidationResult {
  valid: boolean;
  message: string | null;
}

/**
 * Parse a raw `<input type="number">` string into a non-negative integer quantity. Empty
 * string and non-finite input (NaN, partial "-", etc.) both resolve to `0` rather than NaN —
 * the caller treats `0` as "nothing entered" (invalid only once the row is selected).
 */
export function parseQtyInput(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const next = Number(trimmed);
  if (!Number.isFinite(next)) return 0;
  return Math.max(0, Math.floor(next));
}

/**
 * Reject-over-cap validation for one invoice-line row (WS2 / AB#21269). Per the product
 * decision, the UI never silently clamps a user-typed number back down to the cap — instead
 * the raw text stays in the input and this helper flags the row invalid with a message, which
 * the caller uses to block submit/preview until fixed.
 *
 * Only the field matching `entryMode` is checked — the other field is inert while that mode is
 * active (symmetric qty/amount toggle), so it can't produce a stale, misleading invalid state.
 */
export function validateLineEntry(params: {
  entryMode: LineEntryMode;
  selected: boolean;
  parsedQty: number;
  remainingQty: number;
  parsedAmountMinor: number;
  remainingAmountMinor: number;
  currency: string;
  /**
   * Finance feedback (AB#21269, wave 15): the line's own remaining is NOT the whole story — the
   * usable maximum is `min(line remaining, invoice remaining creditable − the rest of this
   * draft)`. Callers pass that effective cap here so the inline message quotes a number that
   * actually works when typed back in. Omitted (older callers/tests) = line remaining only.
   */
  effectiveMaxQty?: number;
  effectiveMaxAmountMinor?: number;
}): LineEntryValidationResult {
  const {
    entryMode, selected, parsedQty, remainingQty, parsedAmountMinor, remainingAmountMinor, currency,
  } = params;
  if (!selected) return { valid: true, message: null };

  if (entryMode === 'qty') {
    const maxQty = Math.min(remainingQty, params.effectiveMaxQty ?? remainingQty);
    if (parsedQty > maxQty) {
      const invoiceLimited = maxQty < remainingQty;
      return {
        valid: false,
        message: `Maximum creditable for this line: ${maxQty}${invoiceLimited ? ' (limited by the invoice’s remaining creditable)' : ''}`,
      };
    }
    return { valid: true, message: null };
  }

  const maxAmountMinor = Math.min(remainingAmountMinor, params.effectiveMaxAmountMinor ?? remainingAmountMinor);
  if (parsedAmountMinor > maxAmountMinor) {
    const invoiceLimited = maxAmountMinor < remainingAmountMinor;
    return {
      valid: false,
      message: `Maximum creditable for this line: ${formatMinorAsDisplay(maxAmountMinor, currency)} ${currency}${invoiceLimited ? ' (limited by the invoice’s remaining creditable)' : ''}`,
    };
  }
  return { valid: true, message: null };
}

/**
 * Validation for a free-form (`custom_line_item`) row. Rows the user hasn't touched yet
 * (`touched: false` — e.g. a freshly added blank template row) are always valid so they don't
 * flash red before the user has typed anything; they're simply excluded from the payload as
 * before. Once touched, an empty description or a zero/negative total blocks submit with a
 * per-row message instead of being silently dropped.
 */
export function validateFreeFormEntry(params: {
  touched: boolean;
  description: string;
  totalMinor: number;
}): LineEntryValidationResult {
  if (!params.touched) return { valid: true, message: null };
  if (!params.description.trim()) {
    return { valid: false, message: 'Enter a description for this line.' };
  }
  if (params.totalMinor <= 0) {
    return { valid: false, message: 'Enter a quantity and unit amount greater than zero.' };
  }
  return { valid: true, message: null };
}

/**
 * Extract the best-available human-readable error message from an axios error (handles the
 * Laravel `sendJsonResponse` shape with `message` + `errors[]`, plus generic axios fallbacks).
 */
export function extractCreditNoteErrorMessage(error: unknown): string {
  if (!error) return 'Credit note creation failed.';
  const e = error as {
    response?: { data?: { message?: string; errors?: unknown } };
    message?: string;
  };
  const data = e.response?.data;
  if (data?.message) {
    let detail = String(data.message);
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = data.errors[0];
      if (typeof first === 'string') detail += ` — ${first}`;
    } else if (data.errors && typeof data.errors === 'object') {
      const firstKey = Object.keys(data.errors as Record<string, unknown>)[0];
      if (firstKey) {
        const val = (data.errors as Record<string, unknown>)[firstKey];
        if (Array.isArray(val) && typeof val[0] === 'string') detail += ` — ${val[0]}`;
        else if (typeof val === 'string') detail += ` — ${val}`;
      }
    }
    return detail;
  }
  if (e.message) return e.message;
  return 'Credit note creation failed.';
}

// ---------------------------------------------------------------------------
// WS3/WS6 — structured error mapping (AB#21269). The backend's create/preview endpoints now
// return 422s shaped `{ errors: { code, detail, ...context } }` (a single error object, not the
// Laravel per-field validation array `extractCreditNoteErrorMessage` above was built for).
// ---------------------------------------------------------------------------

export interface CreditNoteErrorInfo {
  /** Human-readable message — mapped copy for a known `code`, else the legacy extractor's best guess. */
  message: string;
  /** The backend error code, when present (e.g. `'line_over_cap'`). */
  code: string | null;
  /** Raw backend `detail` string, when present — surfaced behind a "Technical detail" toggle. */
  detail: string | null;
}

/** Human copy for the known `errors.code` values the create/preview endpoints can return. */
const CREDIT_NOTE_ERROR_MESSAGES: Record<string, string> = {
  custom_line_auto_tax:
    'Free-form lines aren’t supported on this invoice because Stripe automatic tax is enabled. '
    + 'Enter the credit as an amount against an existing line instead.',
  both_qty_and_amount:
    'A line can be credited by quantity or by amount, not both — clear one before continuing.',
  line_over_cap: 'One of the selected lines exceeds what’s left to credit for that line.',
  line_basis_locked:
    'Stripe locks each line to the entry mode first used to credit it — this line must keep using its original mode (By qty or By amount).',
  over_remaining_creditable: 'This credit note exceeds the invoice’s remaining creditable amount.',
  exceeds_amount_remaining_partially_paid:
    'This exceeds the amount actually paid so far on this partially-paid invoice.',
  preview_failed: 'Could not calculate a preview for this credit note. Please try again.',
  refund_failed: 'Stripe rejected the card refund for this credit note.',
  stripe_error: 'Stripe rejected this request.',
};

/**
 * Maps a backend error response to {@link CreditNoteErrorInfo}. Recognizes the new
 * `errors: { code, detail, ... }` shape first; falls back to
 * {@link extractCreditNoteErrorMessage} (and a null code/detail) for anything else — including
 * network errors and older, pre-rollout error shapes.
 */
export function creditNoteErrorFromResponse(error: unknown): CreditNoteErrorInfo {
  const e = error as { response?: { data?: { errors?: unknown } } };
  const errors = e.response?.data?.errors;
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    const code = (errors as { code?: unknown }).code;
    if (typeof code === 'string' && code) {
      const detailRaw = (errors as { detail?: unknown }).detail;
      const detail = typeof detailRaw === 'string' && detailRaw ? detailRaw : null;
      // Finance feedback (wave 15): `preview_failed` wraps a REAL, actionable reason from the
      // backend/Stripe (e.g. "must not exceed the remaining creditable amount ($690.00)") in
      // `detail` — surface it as the message instead of the generic catch-all, which left users
      // hitting Retry with no way to know what to change.
      const message = code === 'preview_failed' && detail
        ? detail
        : CREDIT_NOTE_ERROR_MESSAGES[code] ?? extractCreditNoteErrorMessage(error);
      return { message, code, detail };
    }
  }
  return { message: extractCreditNoteErrorMessage(error), code: null, detail: null };
}

// ---------------------------------------------------------------------------
// WS6 — pre-create confirmation copy (AB#21269). Shown in the confirm step for card_refund /
// out_of_band, and as the legacy footer sub-line context for unpaid.
// ---------------------------------------------------------------------------

/**
 * Payment-path-specific statement shown before the user commits to creating the credit note
 * (confirm step for card_refund / out_of_band; informational for unpaid). `totalMinor` /
 * `currency` should be the tax-inclusive preview total when available.
 */
export function paymentPathPreCreateStatement(
  path: CreditNotePaymentPath,
  totalMinor: number,
  currency: string,
  opts?: { partiallyPaid?: boolean },
): string {
  const amount = `${formatMinorAsDisplay(totalMinor, currency)} ${currency}`;
  switch (path) {
    case 'card_refund':
      return `Creating this credit note will refund ${amount} to the original card used for payment immediately.`;
    case 'out_of_band':
      return `This records ${amount} to be settled outside Stripe — finance follow-up required (finance@custimoo.com).`;
    case 'unpaid':
      return opts?.partiallyPaid
        ? `This reduces the invoice's amount due by ${amount}. No money moves. The invoice is only `
          + 'partially paid, so the credit is capped at what has actually been charged so far.'
        : `This reduces the invoice's amount due by ${amount}. No money moves.`;
    default:
      return `This will create a credit note for ${amount}.`;
  }
}
